/**
 * Drives any cutscene: owns the animation frame loop, the scene canvas, the
 * current fade, the getput/palette render state, and the audio cues, so a
 * caller only has to say "start", "skip" and "what do I draw now". All the
 * decisions stay in the pure modules — `cutscene-machine` for sequencing,
 * `cutscene-stage` for drawing, `intro-clock` for time (the clock itself has
 * nothing intro-specific in it, so it is reused as-is rather than renamed).
 *
 * Moved out of `shell/intro/intro-player.ts`, which is now a thin wrapper
 * supplying the intro's own `songCueAt` table and `TITLE`'s looping music —
 * see that module. Every other cutscene instead derives its song cue from
 * the tick list itself (`defaultSongCueAt`): each of the five career scripts
 * loads exactly one song, once, near the start (docs/formats/ttm-animation.md's
 * per-file disassembly), so "play it the instant it loads" is both simpler
 * and correct where the intro's silent two-song preload genuinely needed a
 * hand-measured cue table instead.
 */

import type { TtmCommand } from "@red-opus/formats";
import type { ShellAudio } from "../audio/shell-audio.ts";
import { clearLogical, type LogicalSurface } from "../render/canvas-painter.ts";
import type { ShellAssets } from "../render/shell-assets.ts";
import type { IntroClock } from "../intro/intro-clock.ts";
import { advanceCutscene, createCutsceneState, type CutsceneState, skipCutscene } from "./cutscene-machine.ts";
import {
  applyCutsceneCommand,
  composeCutsceneFrame,
  createCutsceneStageCtx,
  type CutsceneStageCtx,
  type FadeState,
  fadeFromCommand,
  fadeOpacity,
} from "./cutscene-stage.ts";
import type { CutsceneTick } from "./cutscene-ticks.ts";

export interface CutscenePlayerDeps {
  readonly scene: LogicalSurface;
  readonly target: LogicalSurface;
  readonly assets: ShellAssets;
  readonly audio: ShellAudio;
  readonly clock: IntroClock;
  /** Which song slot filename (if any) should start audibly at this tick index. Defaults to firing on every `loadSong` command — see module header. */
  readonly songCueAt?: (tickIndex: number, commands: readonly TtmCommand[]) => string | undefined;
  /** Whether the named song should loop once started. Defaults to `false` — every career cutscene is a fixed-length scene, not a held menu backdrop. */
  readonly loopMusic?: (song: string) => boolean;
  /** Called after each composed frame so the caller can blit to the visible canvas. */
  readonly onFrame: () => void;
  readonly onFinished: () => void;
}

export interface CutscenePlayer {
  start(ticks: readonly CutsceneTick[]): void;
  skip(): void;
  /** Tab hidden: hold the clock and the sound together so neither runs on without the other. */
  pause(): void;
  resume(): void;
  stop(): void;
  running(): boolean;
}

/** The `.SNG` filename's bare name, e.g. `"WAREND1.SNG"` -> `"WAREND1"` — `MUSIC_PATHS`' own keying. */
function musicNameOf(id: string): string {
  return id.replace(/\.SNG$/i, "").toUpperCase();
}

/** Fires the instant any `loadSong` command streams by — right for every career cutscene (see module header); the intro overrides this with its own measured table. */
function defaultSongCueAt(_tickIndex: number, commands: readonly TtmCommand[]): string | undefined {
  const command = commands.find((entry) => entry.kind === "loadSong");
  return command ? musicNameOf(command.id) : undefined;
}

/** `PLAY SFX n` targets whichever bank the last `SELECT SND SLOT` chose, so the slot has to be tracked across a tick. */
function routeAudio(commands: readonly TtmCommand[], audio: ShellAudio, slot: number): number {
  let current = slot;
  for (const command of commands) {
    if (command.kind === "selectSoundSlot") current = command.slot;
    else if (command.kind === "playSound") audio.playSfx(current, command.id);
    else if (command.kind === "stopSound") audio.stopSfx(current, command.id);
    else if (command.kind === "stopMusic" || command.kind === "fadeMusic") audio.stopMusic();
  }
  return current;
}

/** Everything one run of the script mutates, kept in one record so the per-tick work can live outside the closure. */
interface CutsceneRun {
  ticks: readonly CutsceneTick[];
  state: CutsceneState;
  fade: FadeState | undefined;
  soundSlot: number;
  stageCtx: CutsceneStageCtx;
  handle: number;
  active: boolean;
}

function applyTick(deps: CutscenePlayerDeps, run: CutsceneRun, tick: CutsceneTick, nowMs: number): void {
  for (const command of tick.commands) {
    applyCutsceneCommand(run.stageCtx, command);
    const next = fadeFromCommand(command, nowMs);
    if (next) run.fade = next;
  }
  run.soundSlot = routeAudio(tick.commands, deps.audio, run.soundSlot);
  const cue = deps.songCueAt ?? defaultSongCueAt;
  const song = cue(tick.index, tick.commands);
  if (song) deps.audio.playMusic(song, deps.loopMusic?.(song) ?? false);
}

export function createCutscenePlayer(deps: CutscenePlayerDeps): CutscenePlayer {
  const run: CutsceneRun = {
    ticks: [],
    state: createCutsceneState(),
    fade: undefined,
    soundSlot: 0,
    stageCtx: createCutsceneStageCtx(deps.scene, deps.assets),
    handle: 0,
    active: false,
  };

  /**
   * A skip can land past any point in the script, including while a sting is
   * still sounding, so the banks are silenced here rather than relying on
   * whatever `STOP SFX` the script would have reached. Music is left alone —
   * exactly the intro's own choice — because what should be sounding once a
   * cutscene ends (nothing, or the caller's own backdrop theme) is a decision
   * for whoever owns `onFinished`, not this player.
   */
  function finish(): void {
    run.active = false;
    cancelAnimationFrame(run.handle);
    deps.clock.stop();
    deps.audio.stopAllSfx();
    run.fade = undefined;
    deps.onFinished();
  }

  function frame(): void {
    if (!run.active || deps.clock.paused()) return;
    const nowMs = deps.clock.elapsedMs();
    const step = advanceCutscene(run.ticks, run.state, nowMs);
    run.state = step.state;
    for (const tick of step.fired) applyTick(deps, run, tick, nowMs);
    const opacity = fadeOpacity(run.fade, nowMs);
    clearLogical(deps.target);
    composeCutsceneFrame(deps.target, deps.scene, opacity);
    deps.onFrame();
    if (run.state.finished && opacity >= 1) {
      finish();
      return;
    }
    run.handle = requestAnimationFrame(frame);
  }

  return {
    start(next) {
      run.ticks = next;
      if (run.ticks.length === 0) {
        finish();
        return;
      }
      run.stageCtx = createCutsceneStageCtx(deps.scene, deps.assets);
      run.state = createCutsceneState();
      run.fade = undefined;
      run.soundSlot = 0;
      deps.clock.start();
      run.active = true;
      run.handle = requestAnimationFrame(frame);
    },
    skip() {
      if (!run.active) return;
      run.state = skipCutscene(run.ticks, run.state);
      finish();
    },
    pause() {
      if (!run.active || deps.clock.paused()) return;
      cancelAnimationFrame(run.handle);
      deps.clock.pause();
      deps.audio.suspend();
    },
    resume() {
      if (!run.active || !deps.clock.paused()) return;
      deps.clock.resume();
      deps.audio.resume();
      run.handle = requestAnimationFrame(frame);
    },
    stop() {
      run.active = false;
      cancelAnimationFrame(run.handle);
      deps.clock.stop();
    },
    running: () => run.active,
  };
}
